// Online orders (stream `online_order:<id>`; ops parity api-commerce/online-ordering.md §1.7, §2.5-§2.6, §2.10, §2.12-§2.13,
// §3.1-§3.2, §4.1): what the storefront places and the register's moves on it. Decided in the cloud: an online order
// arrives over the internet, and the cloud holds its one status, so two registers never move it apart. Core's
// `@radish/core/online` intake rules price it (legacy online math) and move it (the explicit status map, L182).
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { defineEvent } from '../../define/event.ts';
import { secret } from '../../define/secret.ts';
import { additive } from '../../define/upcast.ts';
import { CapabilityUnavailable } from '../../sync/capabilities.ts';
import { DeviceId, EmployeeId, EpochMs, LocationId, Money, ProductId, TableId } from '../../primitives/codecs.ts';
import { LineModifierGroup, ModifierSelection } from '../orders/index.ts';
import { CardNumberInText } from '../ops/support.ts';
import { RefundSettlement } from '../payments/refunds.ts';

const OnlineOrderId = z.uuid({ version: 'v7' });

/** Legacy OnlineOrderStatusType. */
export const OnlineOrderStatus = z
  .enum(['pending-payment', 'incoming', 'accepted', 'in-progress', 'rejected', 'canceled', 'ready', 'not-picked', 'picked-up', 'void'])
  .meta({ id: 'OnlineOrderStatus' });

/** The register's status tiles (legacy OnlineOrderStatsResource keys); rejected counts under canceled. */
export const OnlineOrderTab = z.enum(['incoming', 'in_progress', 'canceled', 'ready', 'picked_up', 'not_picked', 'void']).meta({ id: 'OnlineOrderTab' });

const OnlineCustomer = z
  .object({ firstName: z.string().max(255), lastName: z.string().max(255), email: z.email().max(255), phone: z.string().max(32) })
  .meta({ id: 'OnlineCustomer' });

/**
 * The customer as an online order's facts name them (OnlineOrderPlaced v4): the name the list and the tickets show. The
 * email and phone stay in the cloud (`online.order_contacts`), read on open and by the mails, as card tokens do, so
 * neither a node's feed, its WAL archive and backups, nor a register ever holds them (lane X's review of D3's design).
 */
const OnlineCustomerName = z.object({ firstName: z.string().max(255), lastName: z.string().max(255) }).meta({ id: 'OnlineCustomerName' });

const OnlineModifier = z.object({ name: z.string().min(1).max(120), unitPrice: Money, count: z.int().min(0).max(999) });

const OnlineLine = z
  .object({
    lineId: z.string().min(1).max(64),
    productId: ProductId,
    name: z.string().min(1).max(255),
    unitPrice: Money,
    quantity: z.int().min(1).max(999),
    modifiers: z.array(OnlineModifier).max(200),
    /**
     * OnlineOrderPlaced v3: the same options per modifier set, as the catalog rang them (legacy keeps one
     * `order_item_modifiers` row per set), so kitchen tickets name each set as a sale's do. Absent on older orders.
     */
    modifierGroups: z.array(LineModifierGroup).max(50).optional(),
    note: z.string().max(500).nullable(),
    subtotal: Money,
    tax: Money,
  })
  .meta({ id: 'OnlineLine' });

export const OnlineTotals = z
  .object({ lines: z.array(OnlineLine).min(1).max(200), subtotal: Money, tax: Money, tip: Money, total: Money })
  .meta({ id: 'OnlineTotals' });

// ---- events ----------------------------------------------------------------------------------------------------------
// Cloud facts in the location feed (slice 3 of online orders through the node): a node and its registers pull them,
// only when they declare ONLINE_ORDER_FEED_READER below.
const onlineOrder = { aggregate: 'online_order', deviceConsumed: true } as const;

/**
 * Online orders reach registers through the node (lane D3's design with lane X's review, 2026-10-15; RT-013, OFF-011).
 * A location feed sends an `online_order` fact only to a puller that declares this reader key (version 1) beside its
 * event versions in `maxSchemaVersions`, as `config:payments.processor_account` is declared. Any other puller skips the
 * fact and its cursor moves past it. A node or register built before the stream had a binding would otherwise HOLD on
 * it (`unknown_event_type` is held since #713) and stop pulling everything else, so the flip never waits on fleet builds.
 */
export const ONLINE_ORDER_FEED_READER = 'stream:online_order';
/** The aggregates a feed sends only to a puller that declares the reader key named here. */
export const optInStreamReaders: Readonly<Record<string, string>> = { online_order: ONLINE_ORDER_FEED_READER };

/** The card charge an order waits on (legacy v2): the gateway's dedup invoice number and the store's gateway. */
const OnlineCharge = z.object({ invoiceNo: z.string().min(1).max(64), gateway: z.string().min(1).max(32) });

/** A v3 OnlineOrderPlaced wire payload without the customer's email and phone (the v4 shape). */
function withoutContact(old: unknown): unknown {
  const payload = old as { readonly customer?: { readonly firstName?: unknown; readonly lastName?: unknown } | null };
  const customer = payload.customer;
  return { ...payload, customer: customer === null || customer === undefined ? null : { firstName: customer.firstName, lastName: customer.lastName } };
}

/** OnlineOrderPlaced v5 → v4: the same order without `seq`, which v4 does not know. */
function withoutSeq(newer: unknown): unknown {
  return Object.fromEntries(Object.entries(newer as Record<string, unknown>).filter(([key]) => key !== 'seq'));
}

export const OnlineOrderPlaced = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderPlaced',
  version: 5,
  summary: 'The storefront placed an order, priced by the server from the catalog; it lands incoming (or pending-payment while its card is charged) with its pickup estimate.',
  payload: z.object({
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    number: z.string().min(1).max(32),
    /**
     * v5 (A160, ops plan 2026-10-20-order-numbering): the sequence `number` shows, on the location's counter for the
     * business date (it keeps counting past the 999 wrap). Absent on older orders and on a number from the online
     * fallback series. A server taking the fact in raises its own counter to it (Decided 2's carry-over).
     */
    seq: z.int().positive().optional(),
    /** v4: the name only; the email and phone stay in the cloud (`online.order_contacts`). */
    customer: OnlineCustomerName.nullable(),
    orderName: z.string().max(255).nullable(),
    qrTablePay: z.boolean(),
    tableId: z.string().max(64).nullable(),
    totals: OnlineTotals,
    placedAt: EpochMs,
    estimateReadyAt: EpochMs,
    /** v2: set when the order waits on a card charge. */
    charge: OnlineCharge.nullable().optional(),
  }),
  verifiable: ['onlineOrderId', 'locationId', 'totals'],
  merge: 'lifecycle',
  // v3 → v4 drops the customer's email and phone, which migration 20261015164017 copied into `online.order_contacts`.
  upcastFrom: [additive(1), additive(2), { version: 3, up: withoutContact }, additive(4)],
  // v5 → v4 for a puller that reads only v4 (an older hub or register): the order without its sequence.
  downcastTo: [{ version: 4, down: withoutSeq }],
  pii: ['customer.firstName', 'customer.lastName'],
});

export const OnlineOrderStatusChanged = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderStatusChanged',
  version: 2,
  summary: 'The register moved an online order to another status (accepted is stored as in-progress); a reject, cancel or void may say why.',
  payload: z.object({
    from: OnlineOrderStatus, to: OnlineOrderStatus, at: EpochMs, employeeId: z.string().max(64).nullable(),
    /** v2: the reject, cancel or void reason. */
    reason: z.string().max(255).nullable().optional(),
  }),
  verifiable: ['from', 'to'],
  merge: 'lifecycle',
  upcastFrom: [additive(1)],
});

export const OnlineOrderDelayed = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderDelayed',
  version: 1,
  summary: 'The register pushed an online order\'s pickup estimate back by some minutes.',
  payload: z.object({ minutes: z.int().min(1).max(1440), estimateReadyAt: EpochMs, at: EpochMs }),
  verifiable: ['minutes', 'estimateReadyAt'],
  merge: 'lifecycle',
});

export const OnlineOrderPaymentSettled = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderPaymentSettled',
  version: 1,
  summary: "The gateway approved the order's card charge; the order is incoming.",
  payload: z.object({
    transactionId: z.string().min(1).max(128), cardBrand: z.string().max(32).nullable(), cardLast4: z.string().max(4).nullable(), amount: Money, at: EpochMs,
  }),
  verifiable: ['transactionId', 'amount'],
  merge: 'lifecycle',
});

/**
 * OnlineOrderPaymentDeclined v1 → v2: a v1 fact said "unknown" only by the cloud's own sentence, so its outcome is read
 * from that sentence here, once, for facts recorded before v2 (plan row 15). Nothing else reads the text.
 */
/**
 * The cloud's own sentence on an unknown close: core's PAYMENT_STATUS_UNKNOWN, repeated here because the codegen loads
 * contracts without core's runtime (contracts test `online-payment-unknown` holds the two equal).
 */
export const ONLINE_PAYMENT_UNKNOWN_MESSAGE = 'Payment status unknown.';

function withOutcome(old: unknown): unknown {
  const payload = old as { readonly message?: unknown };
  return { ...payload, outcome: payload.message === ONLINE_PAYMENT_UNKNOWN_MESSAGE ? 'unknown' : 'declined' };
}

/**
 * v2 → v1 for a puller that reads only v1 (an older node or register): the fact without `outcome`. An unknown close
 * carries the cloud's sentence as its message, which is how a v1 reader tells it from a refusal.
 */
function withoutOutcome(newer: unknown): unknown {
  const { outcome, ...rest } = newer as Record<string, unknown> & { readonly outcome?: unknown };
  return outcome === 'unknown' ? { ...rest, message: ONLINE_PAYMENT_UNKNOWN_MESSAGE } : rest;
}

export const OnlineOrderPaymentDeclined = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderPaymentDeclined',
  version: 2,
  summary: "The order's card charge closed unpaid: the gateway declined it, or (v2 `unknown`) never answered and it was not sent again; the order is never listed and its number is a gap.",
  payload: z.object({
    message: z.string().max(255),
    at: EpochMs,
    /** v2 (plan row 15): `declined` the gateway refused; `unknown` the card may or may not have been charged. */
    outcome: z.enum(['declined', 'unknown']),
  }),
  verifiable: ['message', 'outcome'],
  merge: 'lifecycle',
  upcastFrom: [{ version: 1, up: withOutcome }],
  downcastTo: [{ version: 1, down: withoutOutcome }],
});

export const OnlineOrderRefunded = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderRefunded',
  version: 1,
  summary: "A rejected, canceled or voided order's card charge was voided on the gateway, or the gateway refused and the store refunds it by hand.",
  payload: z.object({
    outcome: z.enum(['voided', 'failed']), transactionId: z.string().min(1).max(128), amount: Money, message: z.string().max(255).nullable(), at: EpochMs,
  }),
  verifiable: ['outcome', 'transactionId', 'amount'],
  merge: 'lifecycle',
});

export const OnlineOrderKitchenSent = defineEvent({
  ...onlineOrder,
  name: 'OnlineOrderKitchenSent',
  version: 1,
  summary: "A register claimed an accepted online order's kitchen send: it prints the tickets and sends the KDS, once.",
  payload: z.object({ at: EpochMs, deviceId: z.string().max(64).nullable() }),
  verifiable: ['deviceId'],
  merge: 'lifecycle',
});

/** Every event an online order records, in the order core's `OnlineOrderEvent` union lists them. */
export const onlineOrderEvents = [
  OnlineOrderPlaced, OnlineOrderStatusChanged, OnlineOrderDelayed, OnlineOrderPaymentSettled, OnlineOrderPaymentDeclined, OnlineOrderRefunded,
  OnlineOrderKitchenSent,
] as const;

// ---- views -----------------------------------------------------------------------------------------------------------
/** A row of the register's list (legacy OnlineOrderResource as the list draws it). */
export const OnlineOrderSummary = z
  .object({
    onlineOrderId: OnlineOrderId,
    number: z.string(),
    status: OnlineOrderStatus,
    /** "First Last", or null for a kiosk or guest order (the list shows "(Guest)", Q-ONL-01). */
    customerName: z.string().nullable(),
    orderName: z.string().nullable(),
    qrTablePay: z.boolean(),
    itemCount: z.int().nonnegative(),
    total: Money,
    placedAt: EpochMs,
    estimateReadyAt: EpochMs.nullable(),
    /** When a register claimed the kitchen send; null while the kitchen has not had it (absent on older results). */
    kitchenSentAt: EpochMs.nullable().optional(),
    /**
     * When the register last moved it: the list row's "Picked up at" or "Canceled at" and the closed sidebar's "marked
     * as {status} at {time}" (absent on older results).
     */
    statusChangedAt: EpochMs.nullable().optional(),
  })
  .meta({ id: 'OnlineOrderSummary' });

/** How the order was paid and refunded. */
const OnlineOrderPayment = z
  .object({
    /** An unknown charge reads as `declined` here, as before plan row 15 (core `staffPaymentState`). */
    state: z.enum(['none', 'due', 'settled', 'declined']),
    cardBrand: z.string().nullable(),
    cardLast4: z.string().nullable(),
    /** `pending`: a reject, cancel or void owes the refund and the gateway has not answered yet. */
    refund: z.enum(['none', 'pending', 'voided', 'failed']),
  })
  .meta({ id: 'OnlineOrderPayment' });

// Optional fields: results recorded before they existed still decode (C8).
export const OnlineOrderView = OnlineOrderSummary.extend({
  locationId: LocationId,
  customer: OnlineCustomer.nullable(),
  tableId: z.string().nullable(),
  totals: OnlineTotals,
  payment: OnlineOrderPayment.optional(),
  /** The reject, cancel or void reason. */
  reason: z.string().nullable().optional(),
  /** When it was marked picked up: the customer receipt's "Picked Up" time (absent on older results). */
  pickedUpAt: EpochMs.nullable().optional(),
}).meta({ id: 'OnlineOrderView' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export const OnlineOrderNotFound = defineError({
  code: 'online.order_not_found', httpStatus: 404, message: 'errors.online.order_not_found', retryable: false,
  details: z.object({ onlineOrderId: z.string() }),
});
/** Legacy "Invalid order status update." (422). */
export const OnlineOrderInvalidStatus = defineError({
  code: 'online.invalid_status_update', httpStatus: 422, message: 'errors.online.invalid_status_update', retryable: false,
  details: z.object({ from: OnlineOrderStatus, requested: OnlineOrderStatus }),
});
/** Legacy 503 `offline` / `No open drawers found`: the store is not taking orders right now. */
export const OnlineStoreNotTakingOrders = defineError({
  code: 'online.not_taking_orders', httpStatus: 503, message: 'errors.online.not_taking_orders', retryable: true,
  details: z.object({ reason: z.enum(['offline', 'no_open_till', 'no_store']) }),
});
/** V5: the amount the shopper agreed to is not the order's total. */
export const OnlineAmountMismatch = defineError({
  code: 'online.amount_mismatch', httpStatus: 422, message: 'errors.online.amount_mismatch', retryable: false,
  details: z.object({ total: Money }),
});
/** An item the location does not sell, or choices that do not make a line (core/catalog `RingError`). */
export const OnlineItemInvalid = defineError({
  code: 'online.item_invalid', httpStatus: 422, message: 'errors.online.item_invalid', retryable: false,
  details: z.object({ index: z.int().nonnegative(), reason: z.string() }),
});

/** Another register already printed this order's kitchen tickets. */
export const OnlineKitchenAlreadySent = defineError({
  code: 'online.kitchen_already_sent', httpStatus: 409, message: 'errors.online.kitchen_already_sent', retryable: false,
  details: z.object({ onlineOrderId: z.string() }),
});
/** The order is not accepted, so the kitchen does not have it yet. */
export const OnlineOrderNotAccepted = defineError({
  code: 'online.not_accepted', httpStatus: 422, message: 'errors.online.not_accepted', retryable: false,
  details: z.object({ onlineOrderId: z.string() }),
});
/** Legacy 422 `payment_token`: a storefront order needs a card, and the store a gateway that can charge it. */
export const OnlinePaymentRequired = defineError({
  code: 'online.payment_required', httpStatus: 422, message: 'errors.online.payment_required', retryable: false,
  details: z.object({ reason: z.enum(['no_card', 'not_set_up']) }),
});

/**
 * B1580: a reject, cancel or void of a paid order whose card the register has already refunded in part or whole. Its
 * void would give the whole charge back on top of the refunds, so the store refunds what is left instead.
 */
export const OnlineCardRefunded = defineError({
  code: 'online.card_refunded', httpStatus: 409, message: 'errors.online.card_refunded', retryable: false,
  details: z.object({ onlineOrderId: z.string() }),
});

// ---- commands --------------------------------------------------------------------------------------------------------
const cloud = {
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'An online order arrives over the internet and keeps one status in the cloud, so two registers never move it apart.',
} as const;

export const OrderItemRequest = z.object({
  productId: ProductId,
  variantId: z.string().min(1).max(64).optional(),
  quantity: z.int().min(1).max(999),
  note: z.string().max(500).nullable(),
  modifiers: z.array(ModifierSelection).max(50),
});

export const SubmitOnlineOrder = defineCommand({
  ...cloud,
  name: 'online.submitOrder',
  summary: 'Place an order from the storefront: priced by the server from the catalog, refused while the store is not taking orders.',
  input: z.object({
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    /** Kiosk orders skip the open-hours check and name the tab instead of a customer (legacy `mode=kiosk`). */
    mode: z.enum(['storefront', 'kiosk']),
    items: z.array(OrderItemRequest).min(1).max(200),
    customer: OnlineCustomer.nullable(),
    orderName: z.string().trim().max(255).nullable(),
    /** A QR table-pay order's table (legacy `table_id`). */
    tableId: TableId.nullable(),
    tip: Money,
    /** What the shopper agreed to pay, tip included: must equal the total (V5). */
    amount: Money,
    /**
     * The gateway's one-time card token (legacy `payment_token`). Charged after the order is placed and never kept in
     * an event. A kiosk may leave it out (legacy: no token, no charge).
     */
    paymentToken: secret(z.string().min(1).max(512)).nullable().optional(),
  }),
  // No receipt token here (review of #1912): the cloud records every command's answer and keeps it for replays, so a
  // bearer credential in it would be stored in the clear. Radish Direct's BFF derives the confirmation page's token itself.
  output: OnlineOrderView,
  // A delivery partner, or the Radish Direct storefront's BFF for the one store its host resolved to (D1).
  permission: { session: ['partner', 'storefront'] },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [OnlineOrderPlaced],
  errors: [OnlineStoreNotTakingOrders, OnlineAmountMismatch, OnlineItemInvalid, OnlinePaymentRequired],
  legacy: ['POST /v1/online-store/{id}/orders', 'POST /v2/online-store/{id}/orders'],
});

export const UpdateOnlineOrderStatus = defineCommand({
  ...cloud,
  name: 'online.updateOrderStatus',
  summary: 'Move an online order: accept, mark ready, picked up or not picked up, reject, cancel or void.',
  input: z.object({
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    status: OnlineOrderStatus.exclude(['pending-payment']),
    employeeId: EmployeeId.optional(),
    /** Why a reject, cancel or void happens (legacy void reason); ignored on other moves. */
    reason: z.string().max(255).nullable().optional(),
  }),
  output: OnlineOrderView,
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [OnlineOrderStatusChanged],
  errors: [OnlineOrderNotFound, OnlineOrderInvalidStatus, OnlineCardRefunded],
  legacy: ['PUT /v1/online-orders/{order}/order-status'],
});

export const DelayOnlineOrder = defineCommand({
  ...cloud,
  name: 'online.delayOrder',
  summary: "Push an online order's pickup estimate back by some minutes.",
  input: z.object({ onlineOrderId: OnlineOrderId, locationId: LocationId, minutes: z.int().min(1).max(1440) }),
  output: OnlineOrderView,
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [OnlineOrderDelayed],
  errors: [OnlineOrderNotFound],
  legacy: ['PUT /v1/online-orders/{order}/delay'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const ListOnlineOrders = defineQuery({
  availability: 'cloud',
  cloudReason: cloud.cloudReason,
  name: 'online.orders.list',
  summary: "The location's online orders under one status tile, newest first, with every tile's count.",
  input: z.object({ locationId: LocationId, tab: OnlineOrderTab }),
  output: z.object({ orders: z.array(OnlineOrderSummary), counts: z.record(OnlineOrderTab, z.int().nonnegative()) }),
  permission: { session: 'device' },
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/online-orders?status=', 'GET /v1/online-orders/stats'],
});

export const GetOnlineOrder = defineQuery({
  availability: 'cloud',
  cloudReason: cloud.cloudReason,
  name: 'online.orders.get',
  summary: 'One online order with its lines, customer and totals.',
  input: z.object({ locationId: LocationId, onlineOrderId: OnlineOrderId }),
  output: z.object({ order: OnlineOrderView }),
  permission: { session: 'device' },
  scope: 'location',
  errors: [OnlineOrderNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/online-orders/{order}'],
});

export const ClaimOnlineKitchenSend = defineCommand({
  ...cloud,
  name: 'online.claimKitchenSend',
  summary: "Claim an accepted online order's kitchen send: the first register to claim prints the tickets and sends the KDS.",
  input: z.object({ onlineOrderId: OnlineOrderId, locationId: LocationId, deviceId: DeviceId.optional() }),
  output: OnlineOrderView,
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [OnlineOrderKitchenSent],
  // B1885: declared so the client decodes it with its code; undeclared, it read as `unexpected_response`, which the
  // register's claim takes for a lost answer and asks again for 15 minutes.
  errors: [OnlineOrderNotFound, OnlineKitchenAlreadySent, OnlineOrderNotAccepted, CapabilityUnavailable],
});

/**
 * Email an online order's card receipt to its customer, once (legacy POST /v1/online-orders/{hash}/receipts/email).
 * Every register that hears of a new paid order sends its rendered receipt; only the first is queued.
 */
export const EmailOnlineOrderReceipt = defineCommand({
  ...cloud,
  name: 'online.emailOrderReceipt',
  summary: "Email a paid online order's card receipt to its customer, once, whichever register asks first.",
  input: z.object({
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    /** The order's card receipt as the register rendered it (`emailReceiptText`); never empty. */
    receiptText: secret(z.string().min(1).max(20_000)),
  }),
  /**
   * `queued`: this request queued the email. `already_sent`: an earlier request did (legacy "No pending receipts").
   * `no_customer`: the order has no customer email. `not_paid`: no card charge has settled, so there is no receipt.
   */
  output: z.object({ status: z.enum(['queued', 'already_sent', 'no_customer', 'not_paid']) }),
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [],
  errors: [OnlineOrderNotFound],
  legacy: ['POST /v1/online-orders/{hash}/receipts/email'],
});

export const GetOnlineOrderPayment = defineQuery({
  availability: 'cloud',
  cloudReason: cloud.cloudReason,
  name: 'online.orders.payment',
  summary: "Where the storefront's order stands while its card is charged: waiting, paid (incoming), declined, or unknown (the gateway never answered), with a sentence for the diner.",
  input: z.object({ locationId: LocationId, onlineOrderId: OnlineOrderId }),
  /**
   * `unknown`: the gateway never answered the charge and it was not sent again, so the card may or may not have been
   * charged; the storefront must not offer Pay again (a new Pay is a new order and a new charge). The order records it
   * as its own state (OnlineOrderPaymentDeclined v2, plan row 15); staff views still read it as declined. `message` is a fixed sentence for the diner, never the gateway's text.
   */
  output: z.object({ status: OnlineOrderStatus, payment: z.enum(['none', 'due', 'settled', 'declined', 'unknown']), message: z.string().nullable() }),
  permission: { session: ['partner', 'storefront'] },
  scope: 'location',
  errors: [OnlineOrderNotFound],
  agent: { expose: false },
  legacy: ['POST /v2/online-store/{id}/orders (its 201, 422 and 502 outcomes)'],
});

// ---- late card charges (gate 18 of the Radish Direct plan; A189, ledger L936) --------------------------------------
/**
 * What a later look at the gateway found for an online order whose charge closed as unknown (gate 17's sweep, server
 * `lateSaleSweepJob`): `charged_late` a sale for the order's amount, `charged_other_amount` one for another amount,
 * `several` two approved sales, `voided` a sale voided (by the cloud or by staff), `void_failed` a void the gateway
 * refused, `void_unconfirmed` a void never confirmed, `not_seen` a day with no sale shown at the store's merchant as its
 * key stands now, `not_asked` a day the gateway could not be asked at all.
 */
export const LateSaleFinding = z
  .enum(['charged_late', 'charged_other_amount', 'several', 'voided', 'void_failed', 'void_unconfirmed', 'not_seen', 'not_asked'])
  .meta({ id: 'LateSaleFinding' });

export const LateSaleAlert = z
  .object({
    /** The alert's id (its sync.discrepancies row). */
    alertId: z.uuid(),
    finding: LateSaleFinding,
    onlineOrderId: OnlineOrderId,
    number: z.string(),
    total: Money,
    /** When the order closed as unknown. */
    closedAt: EpochMs,
    /** The gateway's sale the finding names, when it names one. */
    saleId: z.string().nullable(),
    /** The cloud's own words for the finding, kept for support. */
    detail: z.string(),
    recordedAt: EpochMs,
    /** Who marked it handled, when, and their note (at most 250 characters, as Mark as handled and the table take it); null while it is open. */
    handled: z.object({ byName: z.string().nullable(), at: EpochMs, note: z.string().max(250).nullable() }).nullable(),
  })
  .meta({ id: 'LateSaleAlert' });

export const LateSaleAlertNotFound = defineError({
  code: 'online.late_sale_alert_not_found', httpStatus: 404, message: 'errors.online.late_sale_alert_not_found', retryable: false,
  details: z.object({ alertId: z.string() }),
});

export const ListLateSaleAlerts = defineQuery({
  availability: 'cloud',
  cloudReason: 'The alerts are what the cloud found when it asked the card gateway again; only the cloud asks it.',
  name: 'online.lateSales.list',
  summary: "The location's late card-charge alerts: every open one, and those handled in the last 90 days, newest first.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ alerts: z.array(LateSaleAlert), open: z.int().nonnegative() }),
  permission: 'online.manage',
  backofficeAlso: ['online.orders.manage'],
  scope: 'location',
  errors: [],
  agent: { expose: false },
});

export const MarkLateSaleHandled = defineCommand({
  ...cloud,
  name: 'online.markLateSaleHandled',
  summary: 'Mark a late card-charge alert handled, with an optional note; who and when are recorded, and the alert folds away.',
  input: z.object({
    alertId: z.uuid(),
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    note: z.string().trim().max(250).nullable(),
  }),
  /** `already_handled`: someone marked it first; their name and note stand. */
  output: z.object({ status: z.enum(['handled', 'already_handled']) }),
  permission: 'online.manage',
  backofficeAlso: ['online.orders.manage'],
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [],
  errors: [LateSaleAlertNotFound, CardNumberInText],
});

// ---- the cloud's own commands --------------------------------------------------------------------------------------
// Run only by the cloud's online jobs once the gateway answers. They are not in the catalog, so no route mounts them
// and no client can call a charge settled.
const internal = { ...cloud, permission: 'online.manage', risk: 'low', dryRun: false, agent: { expose: false } } as const;

export const SettleOnlinePayment = defineCommand({
  ...internal,
  name: 'online.settlePayment',
  summary: "Record how the order's card charge closed: approved (incoming), declined, or unknown (never answered and not sent again).",
  input: z.object({
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    answer: z.discriminatedUnion('outcome', [
      z.object({ outcome: z.literal('approved'), transactionId: z.string().min(1).max(128), cardBrand: z.string().max(32).nullable(), cardLast4: z.string().max(4).nullable() }),
      z.object({ outcome: z.literal('declined'), message: z.string().max(255) }),
      /** Plan row 15: the cloud's own close of a charge it could not learn; formerly a decline with its sentence. */
      z.object({ outcome: z.literal('unknown') }),
    ]),
  }),
  output: OnlineOrderView,
  emits: [OnlineOrderPaymentSettled, OnlineOrderPaymentDeclined],
  errors: [OnlineOrderNotFound],
});

export const RecordOnlineRefund = defineCommand({
  ...internal,
  name: 'online.recordRefund',
  summary: "Record the gateway's answer to voiding a rejected, canceled or voided order's charge.",
  input: z.object({ onlineOrderId: OnlineOrderId, locationId: LocationId, outcome: z.enum(['voided', 'failed']), message: z.string().max(255).nullable() }),
  output: OnlineOrderView,
  emits: [OnlineOrderRefunded],
  errors: [OnlineOrderNotFound],
});

/** The online card refund's answer was lost (or another ask is sending it): it may have gone through, so it is never sent again. */
export const OnlineRefundUnknown = defineError({
  code: 'online.refund_unknown', httpStatus: 409, message: 'errors.online.refund_unknown', retryable: true,
  details: z.object({ refundTenderId: z.string() }),
});
/** The same refund id was asked before for another order or amount. */
export const OnlineRefundConflict = defineError({
  code: 'online.refund_conflict', httpStatus: 409, message: 'errors.online.refund_conflict', retryable: false,
  details: z.object({ refundTenderId: z.string() }),
});

/**
 * B1563 (Andrew, 2026-10-28, L1033: "Online store's processor"): a picked-up online order's card is refunded on the online
 * store's own gateway, which charged it. Legacy refunded it through the till's gateway on the online `transaction_id`. The
 * cloud reads the gateway and the sale from the order's own stream (never the register's), caps the refund at what the
 * charge has left, and keeps each refund's answer by `refundTenderId`, so Check Again never refunds twice.
 */
export const RefundOnlineSale = defineCommand({
  ...cloud,
  origin: 'any',
  name: 'online.refundSale',
  summary: "Refund part or all of a picked-up online order's card on the online store's gateway, once per refund.",
  input: z.object({
    onlineOrderId: OnlineOrderId,
    locationId: LocationId,
    /** The register's refund id: the operation's key (a second ask answers the first's outcome). */
    refundTenderId: z.uuid(),
    /** What goes back to the card, its tip included. */
    amount: Money,
  }),
  /**
   * `approved` with the gateway's refund reference, or refused with the gateway's (or the cloud's) words. `settledBy`:
   * the refund's answer was lost and a person settled it by hand in the back office (`online.settleCardRefund`); the
   * register's Check Again copies it onto the refund leg's outcome. Absent: the gateway answered.
   */
  output: z.discriminatedUnion('approved', [
    z.object({ approved: z.literal(true), refundRef: z.string().max(128), settledBy: RefundSettlement.optional() }),
    z.object({ approved: z.literal(false), message: z.string().max(255), settledBy: RefundSettlement.optional() }),
  ]),
  permission: { session: 'device' },
  risk: 'low',
  dryRun: false,
  agent: { expose: false },
  emits: [],
  errors: [OnlineOrderNotFound, OnlineRefundUnknown, OnlineRefundConflict],
  legacy: ['POST /order_refund/{order_id} (an online order card, through the till gateway: B1563)'],
});

/** Why a hand settle of an online card refund was refused (core `OnlineCardRefundSettleRefusal`). */
export const OnlineRefundSettleRefused = defineError({
  code: 'online.refund_settle_refused', httpStatus: 409, message: 'errors.online.refund_settle_refused', retryable: false,
  details: z.object({
    refundTenderId: z.string(),
    /**
     * `not_found`: no refund with this id at the location. `already_answered`: the gateway (or a person) answered it.
     * `try_may_be_running`: its try was sent less than 10 minutes ago and may still answer. `needs_reference`: Refunded
     * without the gateway's reference. `gateway_unavailable`: Not refunded while the store's gateway couldn't be asked
     * (an outage or an error); ask again later.
     */
    reason: z.enum(['not_found', 'already_answered', 'try_may_be_running', 'needs_reference', 'gateway_unavailable']),
    /**
     * With `already_answered` (step 4's back-office panel, #2649 review N-1): the answer that stands and who gave it, the
     * gateway (asked again by this settle, or earlier) or a person who settled it by hand first. Absent from older clouds.
     */
    answer: z.object({ by: z.enum(['gateway', 'person']), issued: z.boolean() }).optional(),
  }),
});

/**
 * Ops plan 2026-10-29-settle-unconfirmed-refunds, step 3 (Andrew, A212 and A210: "Yes, manager settles"; L1044): an
 * online card refund whose gateway answer was lost, and that the gateway's lookup can't find, is settled by hand in the
 * back office by Radish staff or the location's manager, after checking the gateway's portal. Refunded needs the
 * portal's reference. The cloud first asks the gateway again; only a refund it still can't find is settled. The refund's
 * journal row then answers the register's Check Again as the gateway would have, naming who settled it.
 */
export const SettleOnlineCardRefund = defineCommand({
  ...cloud,
  origin: 'any',
  name: 'online.settleCardRefund',
  summary: "Settle by hand an online card refund whose gateway answer was lost: Refunded (with the gateway portal's reference) or Not refunded. Needs the void permission.",
  input: z.object({
    locationId: LocationId,
    /** The register's refund id, which keys the refund in the cloud. */
    refundTenderId: z.uuid(),
    settlement: z.discriminatedUnion('issued', [
      z.object({ issued: z.literal(true), gatewayRef: z.string().trim().min(1).max(128) }),
      z.object({ issued: z.literal(false) }),
    ]),
  }),
  /** What the register's Check Again will now answer. */
  output: RefundOnlineSale.output,
  permission: 'orders.void',
  risk: 'high',
  /** A dry run answers what the settle would record from the journal as it is, without asking the gateway. */
  dryRun: true,
  agent: { expose: false },
  emits: [],
  errors: [OnlineRefundUnknown, OnlineRefundSettleRefused],
});

/**
 * Ops plan 2026-10-29-settle-unconfirmed-refunds, step 4's back-office half (mockup
 * https://claude.ai/artifact/9UhPfYZoRDovxuQt9U9EUv; L1044): one online card refund on the Online Orders tab's settle
 * panel. `waiting`: journaled `sent` and never answered; it can be settled by hand from `settleableFrom` (the cloud's
 * 10-minute floor). `settled`: settled by hand, and that settle stands. `overridden`: settled by hand, and the gateway's
 * late answer, which disagreed, replaced it (`settle_overridden_at`, #2603 S-a). Only the settler's display name is told.
 */
const CardRefundHandSettle = z.object({
  /** What the person recorded: Refunded (true) or Not refunded. */
  issued: z.boolean(),
  /** The portal's reference they gave; null for Not refunded. */
  refundRef: z.string().nullable(),
  /** The settler's display name; null when it names nobody the cloud knows. */
  byName: z.string().nullable(),
  at: EpochMs,
});
const CardRefundToSettleFacts = {
  refundTenderId: z.uuid(),
  onlineOrderId: OnlineOrderId,
  /** The online order's number. */
  number: z.string(),
  amount: Money,
  /** When the cloud journaled the try. */
  sentAt: EpochMs,
};
export const OnlineCardRefundToSettle = z
  .discriminatedUnion('state', [
    z.object({ ...CardRefundToSettleFacts, state: z.literal('waiting'), settleableFrom: EpochMs }),
    z.object({ ...CardRefundToSettleFacts, state: z.literal('settled'), settle: CardRefundHandSettle }),
    z.object({
      ...CardRefundToSettleFacts,
      state: z.literal('overridden'),
      /** The hand settle the gateway replaced (its reference is not kept). */
      settle: CardRefundHandSettle.extend({ refundRef: z.null() }),
      /** The gateway's late answer, which stands. */
      gateway: z.object({ issued: z.boolean(), refundRef: z.string().nullable(), at: EpochMs }),
    }),
  ])
  .meta({ id: 'OnlineCardRefundToSettle' });

export const ListCardRefundsToSettle = defineQuery({
  availability: 'cloud',
  cloudReason: "Online card refunds are journaled and settled only in the cloud, which alone asks the online store's gateway.",
  name: 'online.listCardRefundsToSettle',
  summary: "The location's online card refunds still waiting for the gateway's answer (oldest first), then those settled by hand in the last 90 business days (newest settle first).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ refunds: z.array(OnlineCardRefundToSettle), waiting: z.int().nonnegative() }),
  permission: 'orders.void',
  scope: 'location',
  errors: [],
  agent: { expose: false },
});

export const onlineOrderCommands = [
  SubmitOnlineOrder, UpdateOnlineOrderStatus, DelayOnlineOrder, ClaimOnlineKitchenSend, EmailOnlineOrderReceipt, MarkLateSaleHandled, RefundOnlineSale, SettleOnlineCardRefund,
] as const;
export const onlineOrderQueries = [ListOnlineOrders, GetOnlineOrder, GetOnlineOrderPayment, ListLateSaleAlerts, ListCardRefundsToSettle] as const;
/** The cloud's own commands (never mounted): the online jobs run them through the bus. */
export const onlineOrderInternalCommands = [SettleOnlinePayment, RecordOnlineRefund] as const;
